import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Stack,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    FormHelperText,
    FormControlLabel,
    FormGroup,
    Checkbox
} from '@mui/material'
import React, { useState, useEffect } from 'react'
import { createEmployeeLeave, getActiveEmployeesNoLeave, getLeaveConfig } from '../../../../services/LeaveService'

const LeaveEmployeeRegisterModal = ({ open, onClose, onRegisterSuccess }) => {
    const [errors, setErrors] = useState({})
    const [responseMessage, setResponseMessage] = useState('')
    const [showResponseDialog, setShowResponseDialog] = useState(false)
    const [loading, setLoading] = useState(false)
    const [newLeave, setNewLeave] = useState({
        employeeId: null,
        vacationLeave: 0,
        sickLeave: 0,
        maternityLeave: false,
        paternityLeave: false
    })
    const [employees, setEmployees] = useState([])
    const [leaveConfig, setLeaveConfig] = useState({})

    useEffect(() => {
        loadEmployees()
    }, [open])

    useEffect(() => {
        loadLeaveConfig()
    }, [])

    const loadEmployees = async () => {
        try {
            const result = await getActiveEmployeesNoLeave()
            setEmployees(result || [])
        } catch (error) {
            console.error('Error fetching roles:', error)
        }
    }

    const loadLeaveConfig = async () => {
        try {
            const result = await getLeaveConfig()
            setLeaveConfig(result)
            if (leaveConfig) {
                setNewLeave({
                    vacationLeave: result.vacationLeave,
                    sickLeave: result.sickLeave,
                    emergencyLeave: result.emergencyLeave
                })
            }
        } catch (error) {
            console.error('Error fetching roles:', error)
        }
    }

    const validateFields = () => {
        const newErrors = {}
        const requiredFields = ['employeeId', 'vacationLeave', 'sickLeave']

        requiredFields.forEach((field) => {
            if (!newLeave[field]) {
                newErrors[field] = 'This field is required'
            }
        })

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleRegisterInputChange = (e) => {
        const { name, value, type, checked } = e.target
        setNewLeave((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }))
    }

    const handleFormSubmit = async (event) => {
        event.preventDefault()

        if (loading) return

        setLoading(true)

        if (validateFields()) {
            try {
                const result = await createEmployeeLeave(newLeave)

                setResponseMessage(result.error || result.message)
                setShowResponseDialog(true)

                if (!result.error) onRegisterSuccess()
            } catch (error) {
                setResponseMessage('An error occurred while processing the leave request.')
                setShowResponseDialog(true)
                setLoading(false)
            } finally {
                setLoading(false)
                onClose()
            }
        } else {
            setLoading(false)
        }
    }

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>Employee Leave Register</DialogTitle>
                <DialogContent>
                    <form onSubmit={handleFormSubmit}>
                        <Stack spacing={2} className="m-2">
                            <FormControl
                                style={{ marginTop: '16px' }}
                                fullWidth
                                variant="outlined"
                                margin="normal"
                                error={Boolean(errors.employeeId)}
                            >
                                <InputLabel>Employee</InputLabel>
                                <Select
                                    name="employeeId"
                                    value={newLeave.employeeId}
                                    onChange={handleRegisterInputChange}
                                    label="Employee"
                                >
                                    {employees.length > 0 ? (
                                        employees.map((employee) => (
                                            <MenuItem key={employee.id} value={employee.id}>
                                                {employee.firstName} {employee.lastName}
                                            </MenuItem>
                                        ))
                                    ) : (
                                        <MenuItem disabled>No employee available</MenuItem>
                                    )}
                                </Select>
                                {errors.employeeId && <FormHelperText>{errors.employeeId}</FormHelperText>}
                            </FormControl>

                            {/* Reason */}
                            <TextField
                                margin="normal"
                                name="vacationLeave"
                                label="Vacation Leave"
                                fullWidth
                                type="number"
                                variant="outlined"
                                value={newLeave.vacationLeave}
                                onChange={handleRegisterInputChange}
                                error={Boolean(errors.vacationLeave)}
                                helperText={errors.vacationLeave}
                            />

                            <TextField
                                margin="normal"
                                name="sickLeave"
                                label="Sick Leave"
                                fullWidth
                                type="number"
                                variant="outlined"
                                value={newLeave.sickLeave}
                                onChange={handleRegisterInputChange}
                                error={Boolean(errors.sickLeave)}
                                helperText={errors.sickLeave}
                            />

                            <TextField
                                margin="normal"
                                name="emergencyLeave"
                                label="Emergency Leave"
                                fullWidth
                                type="number"
                                variant="outlined"
                                value={newLeave.emergencyLeave}
                                onChange={handleRegisterInputChange}
                                error={Boolean(errors.emergencyLeave)}
                                helperText={errors.emergencyLeave}
                            />

                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={newLeave.maternityLeave}
                                            onChange={handleRegisterInputChange}
                                            name="maternityLeave"
                                        />
                                    }
                                    label="Maternity Leave"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={newLeave.paternityLeave}
                                            onChange={handleRegisterInputChange}
                                            name="paternityLeave"
                                        />
                                    }
                                    label="Paternity Leave"
                                />
                            </FormGroup>
                        </Stack>

                        {/* Actions */}
                        <DialogActions style={{ marginTop: '16px' }}>
                            <Button onClick={onClose} color="secondary">
                                Cancel
                            </Button>
                            <Button type="submit" color="primary" variant="contained" disabled={loading}>
                                {loading ? 'Registering...' : 'Register'}
                            </Button>
                        </DialogActions>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Response Dialog */}
            <Dialog open={showResponseDialog} onClose={() => setShowResponseDialog(false)}>
                <DialogTitle>Response</DialogTitle>
                <DialogContent>
                    <p>{responseMessage}</p>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowResponseDialog(false)} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default LeaveEmployeeRegisterModal
